import { createTheme } from '@mui/material/styles';

import { ColorPalette } from './colorConstants';

interface TypographyVariant {
  appH0: React.CSSProperties;
  appH1: React.CSSProperties;
  appH2: React.CSSProperties;
  appH3: React.CSSProperties;
  appH4: React.CSSProperties;
  appH5: React.CSSProperties;
  appBodyLarge: React.CSSProperties;
  appBodyLargeBold: React.CSSProperties;
  appBodyMedium: React.CSSProperties;
  appBodyMediumBold: React.CSSProperties;
  appButtonText: React.CSSProperties;
  appLabelRegular: React.CSSProperties;
  appLabelBold: React.CSSProperties;
  appNotificationText: React.CSSProperties;
  appCaption: React.CSSProperties;
}

declare module '@mui/material/styles' {
  interface TypographyVariants extends TypographyVariant {}
  interface TypographyVariantsOptions extends Partial<TypographyVariant> {}

  interface Palette {
    appWhite: string;
    questionsForm: {
      cardBg: string;
      formLabel: string;
      formInputText: string;
      formBlue: string;
      formInputUnselected: string;
      tooltipIcon: string;
      formHoverState: string;
      formGrey: string;
      formLightGrey: string;
    };
    tableDivider: string;
    vpTable: {
      recommended: string;
      neutral: string;
      notRecommended: string;
    };
    vpButtonModal: {
      positive: string;
      text: string;
    };
    filterSection: {
      background: string;
      labelColor: string;
      inputTextColor: string;
      radioFill: string;
      radioStrokeUnselected: string;
      radioStrokeSelected: string;
      tooltipIcon: string;
      tooltipBackground: string;
      expandButtonText: string;
      expandButtonHover: string;
    };
  }

  interface PaletteOptions {
    appWhite?: string;
    questionsForm?: {
      cardBg?: string;
      formLabel?: string;
      formInputText?: string;
      formInputUnselected?: string;
      formBlue?: string;
      formHoverState?: string;
      formGrey?: string;
      formLightGrey?: string;
    };
    tableDivider?: string;
    vpTable?: {
      recommended?: string;
      neutral?: string;
      notRecommended?: string;
    };
    vpButtonModal?: {
      positive?: string;
      text?: string;
    };
    filterSection?: {
      background?: string;
      labelColor?: string;
      inputTextColor?: string;
      radioFill?: string;
      radioStrokeUnselected?: string;
      radioStrokeSelected?: string;
      tooltipIcon?: string;
      tooltipBackground?: string;
      expandButtonText?: string;
      expandButtonHover?: string;
    };
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    appH0: true;
    appH1: true;
    appH2: true;
    appH3: true;
    appH4: true;
    appH5: true;
    appBodyLarge: true;
    appBodyLargeBold: true;
    appBodyMedium: true;
    appBodyMediumBold: true;
    appButtonText: true;
    appLabelRegular: true;
    appLabelBold: true;
    appNotificationText: true;
    appCaption: true;
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    'questionsForm.formBlue': true;
    'questionsForm.formHoverState': true;
    appWhite: true;
  }
}

const theme = createTheme({
  palette: {
    primary: {
      main: ColorPalette.PRIMARY,
    },
    secondary: {
      main: ColorPalette.ACCENT_DEFAULT_GREEN,
    },
    appWhite: ColorPalette.WHITE,
    questionsForm: {
      cardBg: ColorPalette.GREY_0,
      formLabel: ColorPalette.ACCENT_MAIN,
      formInputUnselected: ColorPalette.GREY_400,
      formBlue: ColorPalette.BLUE_MAIN,
      formHoverState: ColorPalette.VP_FORM_HOVER_STATE,
      formGrey: ColorPalette.GREY_MAIN,
      formLightGrey: ColorPalette.GREY_400,
    },
    tableDivider: ColorPalette.GREY_100,
    vpTable: {
      recommended: ColorPalette.VP_RECOMMENDED_STATE,
      neutral: ColorPalette.VP_NEUTRAL_STATE,
      notRecommended: ColorPalette.VP_NOT_RECOMMENDED_STATE,
    },
    vpButtonModal: {
      positive: ColorPalette.VP_MODAL_BUTTON,
      text: ColorPalette.GREY_DARK,
    },
  },
  typography: {
    fontFamily: 'Nunito, Arial, sans-serif',
    button: {
      fontSize: '12px',
      lineHeight: '90%',
    },
    appH0: {
      fontWeight: 700,
      fontSize: '48px',
      lineHeight: '120%',
    },
    appH1: {
      fontWeight: 700,
      fontSize: '32px',
      lineHeight: '150%',
    },
    appH2: {
      fontWeight: 700,
      fontSize: '24px',
      lineHeight: '130%',
    },
    appH3: {
      fontWeight: 700,
      fontSize: '18px',
      lineHeight: '130%',
    },
    appH4: {
      fontWeight: 700,
      fontSize: '16px',
      lineHeight: '120%',
    },
    appH5: {
      fontWeight: 700,
      fontSize: '14px',
      lineHeight: '100%',
    },
    appBodyLarge: {
      fontWeight: 400,
      fontSize: '16px',
      lineHeight: '150%',
    },
    appBodyLargeBold: {
      fontWeight: 700,
      fontSize: '16px',
      lineHeight: '150%',
    },
    appBodyMedium: {
      fontWeight: 400,
      fontSize: '14px',
      lineHeight: '130%',
    },
    appBodyMediumBold: {
      fontWeight: 700,
      fontSize: '14px',
      lineHeight: '130%',
    },
    appButtonText: {
      fontWeight: 700,
      fontSize: '12px',
      lineHeight: '120%',
      fontFamily: 'Nunito, sans-serif',
      textTransform: 'uppercase',
    },
    appLabelRegular: {
      fontWeight: 400,
      fontSize: '12px',
      lineHeight: '100%',
    },
    appLabelBold: {
      fontWeight: 700,
      fontSize: '12px',
      lineHeight: '100%',
    },
    appNotificationText: {
      fontWeight: 600,
      fontSize: '14px',
      lineHeight: '120%',
    },
    appCaption: {
      fontWeight: 400,
      fontSize: '12px',
      lineHeight: '120%',
    },
  },
} as const);

const customPalette = {
  accent: {
    main: ColorPalette.ACCENT_MAIN,
    blue: ColorPalette.ACCENT_BLUE,
    defaultGreen: ColorPalette.ACCENT_DEFAULT_GREEN,
    white: ColorPalette.WHITE,
  },
  grey: {
    main: ColorPalette.GREY_MAIN,
    800: ColorPalette.GREY_MAIN,
    400: ColorPalette.GREY_400,
    300: ColorPalette.GREY_300,
    200: ColorPalette.GREY_200,
    100: ColorPalette.GREY_100,
    50: ColorPalette.GREY_50,
    0: ColorPalette.GREY_0,
    dark: ColorPalette.GREY_DARK,
  },
  positive: {
    main: ColorPalette.POSITIVE_MAIN,
    600: ColorPalette.POSITIVE_MAIN,
    400: ColorPalette.POSITIVE_400,
    300: ColorPalette.POSITIVE_300,
    200: ColorPalette.POSITIVE_200,
    100: ColorPalette.POSITIVE_100,
  },
  negative: {
    main: ColorPalette.NEGATIVE_MAIN,
    600: ColorPalette.NEGATIVE_MAIN,
    400: ColorPalette.NEGATIVE_400,
    200: ColorPalette.NEGATIVE_200,
    100: ColorPalette.NEGATIVE_100,
  },
  warning: {
    main: ColorPalette.WARNING_MAIN,
    600: ColorPalette.WARNING_MAIN,
    400: ColorPalette.WARNING_400,
    200: ColorPalette.WARNING_200,
    100: ColorPalette.WARNING_100,
  },
  blue: {
    main: ColorPalette.BLUE_MAIN,
  },
};

export { theme, customPalette };
